import {EAudienceGroupState} from 'src/constants';

import {ETab} from './EditForm/TagsParametersAddresses/definitions';

export default {
  createClient: 'recipient',
  uploadList: 'list',
  editForm: {
    title: 'Recipient details',
    dialogOpen: 'A dialog is open between you and the recipient.',
    newDialogReady: 'Everything is ready to start a dialog',
    toDialog: 'To dialog',
    fields: {
      lastName: 'Last name',
      firstName: 'First name',
      surName: 'Middle name',
      gender: 'Gender',
      birthDate: 'Date of birth',
      phone: 'Phone',
    },
    placeholders: {
      lastName: 'Not specified',
      firstName: 'Not specified',
      surName: 'Not specified',
      gender: 'Not specified',
      birthDate: 'Enter a date',
      phone: 'Enter a number',
    },
    tag: 'Tag',
    [ETab.RESTRICRICTIONS]: {
      head: 'Limitations',
      blacklist: {
        title: 'Blacklist',
        description:
          'Add the recipient to the exclusion list so they don’t receive broadcasts sent from your account',
        blockButton: 'To blacklist',
        unblockButton: 'Exclude',
        blacklisted: 'Added to blacklist',
        confirmModal: {
          title: 'Add to blacklist',
          description:
            'The recipient will be tagged as “Blacklist” and will no longer receive broadcasts sent from your account.',
        },
        unblockModal: {
          title: 'Remove from blacklist?',
          description:
            'The recipient will be able to receive broadcasts sent from your account again. The “Blacklist” tag will be removed.',
        },
        blacklistedSuccess: 'The “Blacklist” tag has been added to the recipient',
        unblacklistedSuccess: 'The “Blacklist” tag has been removed from the recipient',
      },
      whatsappBlock: {
        title: 'Blocking in WhatsApp',
        description:
          'Recipient blocked for spam or other unwanted activity in WhatsApp channels. For recipients who sent an incoming message within the last 24 hours',
        blockButton: 'Block',
        blockButtonAddition: 'Blocking',
        confirmModal: {
          title: 'Block recipient?',
          description:
            'After blocking, the recipient won’t be able to send or receive messages in this channel. Previously received messages will remain saved. ',
          blockSingle: 'Do you want to block recipient in the channel?',
          blockMulti: 'Select the channels in which you want to block recipient',
          emptyDescription:
            'The recipient cannot be blocked. He hasn’t sent incoming messages in the last 24 hours.',
        },
        unblockModal: {
          title: 'Unblock recipient?',
          descriptionSingle: 'Unblock recipient in the channel',
          description: 'Select the channels in which you want to unblock recipient',
        },
        blocked: 'Blocked in WhatsApp',
        unblockButton: 'Unblock',
        blockedInChannels: 'Blocked in the following channels:',
        unblockSuccess: 'Recipient unblocked in the {{name}} channel',
        unblockError: 'Failed to unblock recipient in the {{name}} channel',
        blockSuccess: 'Recipient blocked in the {{name}} channel',
        blockError: 'Failed to block recipient in the {{name}} channel',
      },
      unsubscribe: {
        title: 'WhatsApp unsubscription',
        description: 'The recipient has unsubscribed from WhatsApp channels',
        unsubscribeButton: 'Unsubscribe',
        subscribeButton: 'Resubscribe',
        addUnsubscribedSubject: 'Unsubscribe',
        userUnsubscribed: 'This recipient is unsubscribed in some channels',
        unsubscribedSubjects: 'Unsubscribed in channels:',
        successSubscribe: 'The recipient subscription restored for {{subjects}}',
        errorSubscribe: 'Failed to restore the recipient subscription for {{subjects}}',
        successUnsubscribe: 'The recipient unsubscribed from {{subjects}}',
        errorUnsubscribe: 'Failed to unsubscribe the recipient from {{subjects}}',
        subjects_one: '{{subjects}}',
        subjects_few: '{{subjects}}',
        subjects_many: '{{subjects}}',
        subscribeModal: {
          title: 'Resubscribe recipient',
          description: 'Select the channels where the recipient will receive broadcasts again',
          descriptionSingle: 'The recipient will receive broadcasts again in this channel',
          submitButton: 'Resubscribe',
        },
        unsubscribeModal: {
          title: 'Unsubscribe recipient',
          description:
            'After unsubscribing, the recipient won’t receive broadcasts in the selected channels.\n\n' +
            'Choose the channels to unsubscribe the recipient from.',
          descriptionSingle:
            'After unsubscribing, the recipient won’t receive broadcasts in this channel',
          submitButton: 'Unsubscribe',
        },
      },
      errors: {
        selectSubjects: 'Select channels',
      },
      hide: 'Collapse',
      showAll: '+ {{count}} more',
    },
    [ETab.TAGS]: {
      head: 'Recipient tags',
      content: {
        title: 'Recipient tags',
        action: 'Assign tag',
        notification: 'Tags have been successfully added',
      },
      empty: {
        title: 'This recipient has no tags yet',
        text: 'You can add tags right away or later, when you have more information about the recipient',
        action: 'Assign tag',
      },
    },
    [ETab.PARAMETERS]: {
      head: 'Variables',
      content: {
        title: 'Custom variables',
        description:
          'Such variables are added manually or uploaded together with a list of recipients. They can be used in broadcasts, dialogs and scripts. Variables can be added, changed and deleted.',
        action: 'Assign variable',
        nameLabel: 'Variable',
        valueLabel: 'Value',
        showAll: 'Show all',
        hideAll: 'Collapse',
      },
      system: {
        title: 'System variables',
        description:
          'Pulse receives this recipient data from the connected channels automatically. It can be a user identifier in the channel, username, display name and other data. System variables are read-only — they cannot be changed or deleted.',
        nameLabel: 'Variable',
        valueLabel: 'Value',
        groupSeparator: 'and',
      },
    },
  },
  editGroupForm: {
    title: 'Upload document',
    subtitle: `Upload a new recipient list and add attributes.\nUploading a list of new recipients might take some time.`,
    pendingHint: `<0>Please do not leave the page.</>\nIf you leave the page, your progress will not be saved.`,
    fileButton: '.xlsx',
    anotherFileButton: 'Upload another document',
    chooseTag: 'Select attributes',
    tags: 'Tags',
    tag: 'tag',
    chooseFromTagTree: 'Tags tree',
    tagIds: 'Assign tags to your list',
    tagIdsHint: 'Select the tags that will be applied to the list',
    variable: 'variable',
    variables: 'Recipient variables',
    variableIds: 'Assign variables to the recipients in the list',
    variableIdsHint: 'Select a variable name for each column',
    chooseVariable: 'Choose variable',
    editTitle: 'Add list attributes',
    uploadTitle: 'Recipient list is uploading',
    uploadSubtitle: `Uploading and updating your audience is going to take some time.
    <0>You don't have to stay on this page.</0> The process will continue if you open another page.
    Come back to <1>this page</1> to check the current upload status.`,
    errorLoadingFile:
      'Recipient list upload was interrupted. The list of the recipients {{fileName}} has not been uploaded.',
    flowParticipation: 'Script participation',
    flowHint: 'Run a script for the recipients in this list, starting with an external trigger',
    selectFlow: 'Select one or more scripts',
  },
  editGroupFormModal: {
    title: 'Upload document',
    subtitle: `You can add a new list and tag it to the already selected recipient audience. Once the upload is finished, you will be able to select whether the audience with this tag must be included in the broadcast or excluded from it.<br/><br/>Uploading a list of new clients might take some time.`,
    fileButton: 'Attach file',
    anotherFileButton: 'Attach another file',
  },
  pickAudienceForm: {
    title: 'What to do with this audience?',
    subtitle: {
      tagsChosen: 'The audience with these tags must be:',
      tagsNotChosen:
        'You uploaded a list of recipients and did not assign any tags to it. We have assigned a tag with the file name to this list. You can find the list in the Broadcast Lists category.',
    },
    whichGroup: 'To which group should the audience belong?',
    includeToBroadcast: 'Added to the broadcast',
    excludeFromBroadcast: 'Excluded from the broadcast',
    button: {
      continue: 'Continue',
      back: 'Back',
    },
    group: 'Group',
    tagWarning: {
      [EAudienceGroupState.excluded]:
        'The audience with this tag is already included in the broadcast. You cannot exclude it from the broadcast at the same time.',
      [EAudienceGroupState.included]:
        'The audience with this tag has already been excluded from the broadcast. You cannot simultaneously include it while it is excluded.',
    },
  },
  modal: {
    uploadingTitle: 'Recipient list loading progress',
    resultTitle: 'Recipient list loading result',
    successInfoWithFile:
      'Successfully <b>uploaded {{successCount}}</b> out of {{totalCount}} records, or <b>{{percent}}%</b> of the list {{fileName}}',
    successInfo:
      'Successfully <b>uploaded {{successCount}}</b> out of {{totalCount}} records, or <b>{{percent}}%</b> of the list',
    errorInfo_one: '. {{errorCount}} record was not uploaded. <0><b>Download the list</b></0>',
    errorInfo_other: '. {{errorCount}} records were not uploaded. <0><b>Download the list</b></0>',
  },
  error: {
    atLeastOnePhone: 'Select the phone number column',
    atLeastOnePhoneMessage:
      'One of the columns in the list must have the header "Phone" and contain phone numbers',
    failedToLoadParameters: "There was an error while getting the recipient's variables",
    failedToLoadTags: "There was an error while getting the recipient's tags",
    failedToLoadAddresses: "There was an error while getting the recipient's identifiers",
  },
  list: {
    searchPlaceholder: 'Quick search by phone number',
    name: 'Full name',
    emptyName: 'Name not specified',
    phone: 'Identifier',
    addButton: 'to database',
    addClient: 'Add recipient',
    addList: 'Add list',
  },
  notification: {
    emptyFile: 'File is empty, please upload other file',
  },
  dialog: {
    remove: {
      title: 'Do you really want to delete the recipient?',
      body: 'We will irrevocably lose the record and all information about the recipient',
    },
  },
  empty: {
    title: 'Nothing here yet',
    subtitle: 'You don’t have any recipients yet. You can add one recipient or upload a whole list',
  },
  noDataWithFilters: {
    title: ':(',
    text: 'We could not find a recipient with this phone number or identifier',
    hint: 'Please check if the data you entered is correct',
    button: 'Back to list',
  },
  filters: {
    selectTags: 'Select tags',
    recipientTags: 'Recipient tags',
    recipientRestrictions: 'Recipient limitations',
    whatsAppBlock: 'Blocking in WhatsApp',
    blacklist: 'Blacklist',
    parameters: 'Variables',
    unsubscribed: 'WhatsApp unsubscription',
  },
};
